import React, { useMemo } from 'react'
import ReactHintFactory from 'react-hint'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router-dom'
import { Footer } from '@frontend/components/agromarket/components/Footer'
import { RegionServiceProvider } from '@frontend/components/agromarket/components/RegionSelector'
import { RouterProvider } from '@frontend/components/agromarket/core/router'
import { PWAProvider, useInstallPWA } from '@frontend/pole-pwa'
import { Toaster } from '@frontend/pole-ui/lib/components/Notification'
import { SSRProvider } from '@frontend/ui-kit/agromarket/core/ssr'
import { MediaQueryProvider } from '@pole/media-queries/agromarket'
import { IntlProvider } from 'rsuite'
import ru from 'rsuite/lib/IntlProvider/locales/ru_RU'
import { registerApi } from '@/api/client/base'
import { AppHeader } from '@/App/components/AppHeader/AppHeader'
import { DeliveryRegionSelector } from '@/App/components/AppHeader/DeliveryRegionSelector/DeliveryRegionSelector'
import AppPWANotification from '@/App/components/AppPWANotification/AppPWANotification'
import { useOnAppMount } from '@/App/hooks/useOnAppMount'
import { AppAccessControlProvider } from '@/components/composed/AppAccessControlProvider/AppAccessControlProvider'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import ErrorApiModal from '@/components/composed/modals/ErrorApiModal/ErrorApiModal'
import { TelegramSubscribeOverlayBanner } from '@/components/composed/TelegramSubscribeOverlayBanner/TelegramSubscrbeOverlayBanner'
import { getAuthState } from '@/logic/auth/reducer'
import { FinancingWidgetCallback } from '@/pages/Financing/components/FinancingWidgetCallback'
import { Redirect404 } from '@/router/components'
import { useHref, useLocation, useNavigate } from '@/router/hooks'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import 'cross-fetch/polyfill'
import messages from '../commonMessages/messages.json'
import { CookieAgreement, MobileNavBar, Modal } from '../components/composed'
import { Breadcrumbs } from '../components/ui'
import { getLocation, hasWebpSupport, is404Page } from '../logic/app/reducer'
import { getCurrentLocale } from '../logic/translation/selectors'
import { injectMessages } from '../logic/translation/utils'
import type { RootState } from '../redux/interfaces'
import { ROUTES } from '../routes'
import { useFormatDate } from '../utils/hooks/format/useFormatDate'
import AppFixedOverlayContainer from './components/AppFixedOverlayContainer/AppFixedOverlayContainer'
import { AppRoute } from './components'
import 'swiper/swiper.scss'
import 'keen-slider/keen-slider.min.css'
import 'rsuite/dist/styles/rsuite-default.min.css'
import 'react-hint/css/index.css'
import './app.scss'

addModalToWellKnown({ ErrorApiModal })

const ReactHint = ReactHintFactory(React)

const Routes = React.memo(() => (
  <Switch>
    {ROUTES.map(routeProps => (
      <AppRoute {...routeProps} />
    ))}
    <Route key="404" component={() => <Redirect404 />} />
  </Switch>
))

const RsuiteLocale: Record<string, Object> = {
  ru,
}

injectMessages(messages)

// Регистрация всех axios клиентов на клиенте
registerApi()

const mapState = (state: RootState) => ({
  auth: getAuthState()(state),
  currentLanguage: getCurrentLocale()(state),
  withWebp: hasWebpSupport()(state),
  is404: is404Page()(state),
  location: getLocation()(state),
})

const App: React.FC = () => {
  const { pathname } = useLocation()
  const { currentLanguage, withWebp, is404, auth } = useSelector(mapState)
  const { formatDate } = useFormatDate()

  useOnAppMount()

  const navigate = useNavigate()
  const useRouterHref = useHref()

  const { isMobile, isTablet, isSmall } = useDeviceType()

  const accessControlContext = useMemo(
    () => ({
      auth,
    }),
    [auth],
  )

  return (
    <SSRProvider>
      <RouterProvider baseRouter={{ navigate, useHref: useRouterHref }} getCurrentPath={() => pathname || '/'}>
        <IntlProvider locale={RsuiteLocale[currentLanguage]} formatDate={(date, dateFormat) => formatDate(date, dateFormat)}>
          <MediaQueryProvider>
            <AppAccessControlProvider context={accessControlContext}>
              <PWAProvider>
                <RegionServiceProvider />
                <Modal />
                <ReactHint autoPosition events />
                <div className={`app app_${withWebp ? 'with-webp' : 'no-webp'}`}>
                  <AppPWANotification />

                  <AppFixedOverlayContainer>
                    <FinancingWidgetCallback />
                    <TelegramSubscribeOverlayBanner />
                    <CookieAgreement />
                  </AppFixedOverlayContainer>

                  <AppHeader />
                  <DeliveryRegionSelector />

                  {(isMobile || isTablet || isSmall) && <MobileNavBar />}
                  <main data-page={pathname}>
                    {!is404 && !isMobile && (
                      <div className="page-wrapper">
                        <Breadcrumbs />
                      </div>
                    )}
                    <Routes />
                  </main>
                  {!is404 && <Footer useInstallPWA={useInstallPWA} />}
                  <Toaster />
                </div>
              </PWAProvider>
            </AppAccessControlProvider>
          </MediaQueryProvider>
        </IntlProvider>
      </RouterProvider>
    </SSRProvider>
  )
}

export default App
